"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Edit,
  Check,
  ArrowLeft,
  Trash,
  Truck,
  User as UserIcon,
  FileText,
} from "lucide-react";
import ImageComponent from "../../../../components/form-components/ImageComponent";
import {
  useFetchVehicleById,
  useUpdateVehicle,
  useDeleteVehicle,
} from "../../../../hooks/useFetchVehicle";
import { Vehicle } from "../../../../types/Vehicle";
import LoadingComponent from "../../../../components/form-components/LoadingComponent";
import Spinner from "../../../Spinner";
import CustomDropdown from "../../../CustomDropdown";

const VEHICLE_TYPES = [
  { label: "Open Body", value: "Open Body" },
  { label: "Container", value: "Container" },
  { label: "Trailer", value: "Trailer" },
  { label: "Dumper", value: "Dumper" },
];
const VERIFIED_OPTIONS = [
  { value: true, label: "Verified" },
  { value: false, label: "Not Verified" },
];

type UpdatedFields = Partial<
  Omit<Vehicle, "drivingLicence" | "rc" | "panCard" | "aadharCard">
> & {
  drivingLicence?: File;
  rc?: File;
  panCard?: File;
  aadharCard?: File;
  [key: string]: any;
};

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const VehicleInfo: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { vehicle, loading, error, fetchVehicle } =
    useFetchVehicleById(vehicleId);
  const { updateVehicle, loading: updating } = useUpdateVehicle();
  const { deleteVehicle, loading: deleting } = useDeleteVehicle();

  const [isEditing, setIsEditing] = useState(false);
  const [vehicleData, setVehicleData] = useState<Vehicle | null>(null);
  const [updatedFields, setUpdatedFields] = useState<UpdatedFields>({});
  const [imageErrors, setImageErrors] = useState({
    drivingLicence: "",
    rc: "",
    panCard: "",
    aadharCard: "",
  });

  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isVerifiedDropdownOpen, setIsVerifiedDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const verifiedDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(typeDropdownRef, () => setIsTypeDropdownOpen(false));
  useClickOutside(verifiedDropdownRef, () => setIsVerifiedDropdownOpen(false));

  useEffect(() => {
    if (vehicle) {
      setVehicleData(vehicle);
    }
  }, [vehicle]);

  const handleFieldChange = (field: keyof Vehicle, value: any) => {
    setVehicleData((prev) => (prev ? { ...prev, [field]: value } : null));
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
  };

  const validateFile = useCallback((file: File, fieldName: string) => {
    const validTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      setImageErrors((prev) => ({
        ...prev,
        [fieldName]: "Invalid file type.",
      }));
      return false;
    }
    if (file.size > maxSize) {
      setImageErrors((prev) => ({
        ...prev,
        [fieldName]: "File size exceeds 5MB.",
      }));
      return false;
    }
    setImageErrors((prev) => ({ ...prev, [fieldName]: "" }));
    return true;
  }, []);

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        const fieldName = e.target.name as keyof typeof imageErrors;
        if (validateFile(file, fieldName)) {
          setUpdatedFields((prev) => ({ ...prev, [fieldName]: file }));
        }
      }
    },
    [validateFile],
  );

  const handleUpdate = async () => {
    if (!vehicleId || !vehicleData || Object.values(imageErrors).some((e) => e))
      return;

    const formData = new FormData();
    Object.entries(updatedFields).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (value !== null && value !== undefined) {
        // *** FIX APPLIED HERE ***
        // When sending boolean values in FormData, they get converted to strings ("true" or "false").
        // Some backends don't handle this gracefully and throw a 500 error.
        // A common workaround is to send '1' for true and '0' for false.
        // The ideal fix is for the backend to correctly parse the string "true".
        if (typeof value === "boolean") {
          formData.append(key, value ? "true" : "false"); // Sending "true" or "false" string, which is standard. If this fails, try '1' and '0'.
        } else {
          formData.append(key, String(value));
        }
      }
    });

    // If no fields were updated, don't send the request.
    if (Array.from(formData.keys()).length === 0) {
      setIsEditing(false);
      return;
    }

    try {
      const updatedVehicleData = await updateVehicle(vehicleId, formData);
      if (updatedVehicleData) {
        setVehicleData(updatedVehicleData);
        setIsEditing(false);
        setUpdatedFields({});
        fetchVehicle();
      }
    } catch (err) {
      console.error("Failed to update vehicle:", err);
    }
  };

  const handleDelete = async () => {
    if (!vehicleId) return;
    if (
      window.confirm(
        "Are you sure you want to delete this vehicle? This action cannot be undone.",
      )
    ) {
      try {
        await deleteVehicle(vehicleId);
        navigate("/vehicles");
      } catch (err) {
        console.error("Failed to delete vehicle", err);
      }
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!vehicleData)
    return (
      <div className="p-6">
        No vehicle data found. The ID may be invalid or the vehicle has been
        removed.
      </div>
    );

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {vehicleData.vehicleNumber}
              </h1>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${vehicleData.isVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
              >
                {vehicleData.isVerified ? "Verified" : "Not Verified"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-colors disabled:text-gray-400 disabled:bg-transparent"
            >
              {deleting ? <Spinner /> : <Trash size={20} />}
            </button>
            {isEditing ? (
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-gray-400 w-28 justify-center"
              >
                {updating ? <Spinner /> : <Check size={20} />}
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-300 transition-colors w-28 justify-center"
              >
                <Edit size={20} />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <InfoCard title="Vehicle Information" icon={<Truck />}>
              <DetailItem
                label="Vehicle Number"
                value={vehicleData.vehicleNumber}
                name="vehicleNumber"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("vehicleNumber", e.target.value)
                }
              />
              <CustomDropdown
                label="Vehicle Type"
                value={vehicleData.vehicleType}
                options={VEHICLE_TYPES}
                isOpen={isTypeDropdownOpen}
                setIsOpen={setIsTypeDropdownOpen}
                onSelect={(value) => handleFieldChange("vehicleType", value)}
                isEditing={isEditing}
                dropdownRef={typeDropdownRef}
              />
            </InfoCard>
            <InfoCard title="Driver Details" icon={<UserIcon />}>
              <DetailItem
                label="Driver Name"
                value={vehicleData.driverName}
                name="driverName"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("driverName", e.target.value)
                }
              />
              <DetailItem
                label="Driver Phone"
                value={vehicleData.phoneNumber}
                name="phoneNumber"
                type="tel"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("phoneNumber", e.target.value)
                }
              />
            </InfoCard>
            <InfoCard title="Documents" icon={<FileText />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ImageComponent
                  title="Driving Licence"
                  imageUrl={`https://movingrolls.online${vehicleData.drivingLicence}`}
                  onFileChange={isEditing ? handleImageChange : undefined}
                  error={imageErrors.drivingLicence}
                  name="drivingLicence"
                />
                <ImageComponent
                  title="RC Document"
                  imageUrl={`https://movingrolls.online${vehicleData.rc}`}
                  onFileChange={isEditing ? handleImageChange : undefined}
                  error={imageErrors.rc}
                  name="rc"
                />
                <ImageComponent
                  title="PAN Card"
                  imageUrl={`https://movingrolls.online${vehicleData.panCard}`}
                  onFileChange={isEditing ? handleImageChange : undefined}
                  error={imageErrors.panCard}
                  name="panCard"
                />
                <ImageComponent
                  title="Aadhar Card"
                  imageUrl={`https://movingrolls.online${vehicleData.aadharCard}`}
                  onFileChange={isEditing ? handleImageChange : undefined}
                  error={imageErrors.aadharCard}
                  name="aadharCard"
                />
              </div>
            </InfoCard>
          </div>
          <div className="lg:col-span-1 space-y-6">
            <InfoCard title="Status" icon={<Check />}>
              <CustomDropdown
                label="Verified"
                value={vehicleData.isVerified}
                options={VERIFIED_OPTIONS}
                isOpen={isVerifiedDropdownOpen}
                setIsOpen={setIsVerifiedDropdownOpen}
                onSelect={(value) => handleFieldChange("isVerified", value)}
                isEditing={isEditing}
                dropdownRef={verifiedDropdownRef}
              />
            </InfoCard>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[#3c0b0b]">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  label: string;
  value: string;
  name: string;
  isEditing: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}> = ({ label, value, name, isEditing, onChange, type = "text" }) => (
  <div>
    <label className="text-sm text-gray-500">{label}</label>
    {isEditing ? (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full p-2 border rounded-md mt-1"
      />
    ) : (
      <p className="font-medium text-gray-800">{value || "-"}</p>
    )}
  </div>
);

export default VehicleInfo;
